import {
  startOfDayInTz,
  endOfDayInTz,
  startOfWeekInTz,
  endOfWeekInTz,
  startOfMonthInTz,
  endOfMonthInTz,
  subDays,
  subMonths,
  DEFAULT_TIMEZONE,
  type WeekStart,
} from './timezone';

export type DatePreset = 
  | 'all' 
  | 'today' 
  | 'yesterday' 
  | 'this_week' 
  | 'last_week' 
  | 'last_7_days'
  | 'last_14_days'
  | 'last_30_days'
  | 'this_month' 
  | 'last_month' 
  | 'custom';

export interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  all: 'All Time',
  today: 'Today',
  yesterday: 'Yesterday',
  this_week: 'This Week',
  last_week: 'Last Week',
  last_7_days: 'Last 7 Days',
  last_14_days: 'Last 14 Days',
  last_30_days: 'Last 30 Days',
  this_month: 'This Month',
  last_month: 'Last Month',
  custom: 'Custom',
};

/**
 * Get date range from a preset, calculated in the specified timezone.
 * @param preset The date preset to calculate
 * @param timezone IANA timezone string (e.g., 'Australia/Sydney'). Defaults to Australia/Sydney.
 * @param weekStartsOn 1 = Monday (default), 0 = Sunday — from the tenant locale
 * @returns DateRange with from and to dates in UTC (for database queries)
 */
export function getDateRangeFromPreset(
  preset: DatePreset,
  timezone: string = DEFAULT_TIMEZONE,
  weekStartsOn: WeekStart = 1,
): DateRange {
  // Use raw UTC date - the startOfDayInTz/endOfDayInTz functions handle the timezone conversion
  const now = new Date();
  
  switch (preset) {
    case 'all':
      return { from: undefined, to: undefined };
      
    case 'today':
      return {
        from: startOfDayInTz(now, timezone),
        to: endOfDayInTz(now, timezone),
      };
      
    case 'yesterday': {
      const yesterday = subDays(now, 1);
      return {
        from: startOfDayInTz(yesterday, timezone),
        to: endOfDayInTz(yesterday, timezone),
      };
    }
      
    case 'this_week':
      // Start of week to TODAY (not full week)
      return {
        from: startOfWeekInTz(now, timezone, weekStartsOn),
        to: endOfDayInTz(now, timezone),
      };

    case 'last_week': {
      const lastWeekDate = subDays(now, 7);
      return {
        from: startOfWeekInTz(lastWeekDate, timezone, weekStartsOn),
        to: endOfWeekInTz(lastWeekDate, timezone, weekStartsOn),
      };
    }
      
    case 'last_7_days': {
      const sevenDaysAgo = subDays(now, 7);
      return {
        from: startOfDayInTz(sevenDaysAgo, timezone),
        to: endOfDayInTz(now, timezone),
      };
    }
      
    case 'last_14_days': {
      const fourteenDaysAgo = subDays(now, 14);
      return {
        from: startOfDayInTz(fourteenDaysAgo, timezone),
        to: endOfDayInTz(now, timezone),
      };
    }
    
    case 'last_30_days': {
      const thirtyDaysAgo = subDays(now, 30);
      return {
        from: startOfDayInTz(thirtyDaysAgo, timezone),
        to: endOfDayInTz(now, timezone),
      };
    }
      
    case 'this_month':
      // 1st of month to TODAY (not full month)
      return {
        from: startOfMonthInTz(now, timezone),
        to: endOfDayInTz(now, timezone),
      };
      
    case 'last_month': {
      const lastMonth = subMonths(now, 1);
      return {
        from: startOfMonthInTz(lastMonth, timezone),
        to: endOfMonthInTz(lastMonth, timezone),
      };
    }
      
    case 'custom':
      return { from: undefined, to: undefined };
      
    default:
      return { from: undefined, to: undefined };
  }
}
